import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@/auth/session";
import {
  applyCuts,
  getCutsEligible,
  getSettingsSnapshot,
  listCuts,
  listSectors,
  restoreCuts,
  type CutAppliedItem,
  type CutEligibleItem,
  type SectorItem,
} from "@/lib/api";

type Tab = "elegibles" | "cortados";

export default function CortesServicioPage() {
  const navigate = useNavigate();
  const { session } = useSession();

  const [tab, setTab] = useState<Tab>("elegibles");
  const [sectors, setSectors] = useState<SectorItem[]>([]);
  const [defaultMonths, setDefaultMonths] = useState(2);

  // Filtros
  const [sectorFilter, setSectorFilter] = useState("");
  const [monthsFilter, setMonthsFilter] = useState("");
  const [amountFilter, setAmountFilter] = useState("");

  // Elegibles
  const [eligible, setEligible] = useState<CutEligibleItem[]>([]);
  const [loadingEligible, setLoadingEligible] = useState(false);
  const [eligibleError, setEligibleError] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<{ applied: number; errors: string[] } | null>(null);

  // Cortados
  const [cuts, setCuts] = useState<CutAppliedItem[]>([]);
  const [loadingCuts, setLoadingCuts] = useState(false);
  const [cutsError, setCutsError] = useState("");
  const [selectedCutIds, setSelectedCutIds] = useState<Set<string>>(new Set());
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState<{ restored: number; errors: string[] } | null>(null);

  useEffect(() => {
    if (!session) return;
    void Promise.all([listSectors(session), getSettingsSnapshot(session)]).then(([s, snap]) => {
      setSectors(s);
      setDefaultMonths(snap.organization?.morosidadMonths ?? 2);
      setMonthsFilter(String(snap.organization?.morosidadMonths ?? 2));
    });
  }, [session]);

  useEffect(() => {
    if (tab === "cortados") void loadCuts();
  }, [tab, session]);

  async function loadEligible(): Promise<void> {
    if (!session) return;
    setLoadingEligible(true);
    setEligibleError("");
    setSelectedIds(new Set());
    setApplyResult(null);
    try {
      const rows = await getCutsEligible(session, {
        minMonths: monthsFilter ? Number(monthsFilter) : defaultMonths,
        sectorName: sectorFilter || undefined,
        minAmount: amountFilter ? Number(amountFilter.replace(/\D/g, "")) : undefined,
      });
      setEligible(rows);
    } catch (e) {
      setEligibleError(String(e));
    } finally {
      setLoadingEligible(false);
    }
  }

  async function loadCuts(): Promise<void> {
    if (!session) return;
    setLoadingCuts(true);
    setCutsError("");
    setSelectedCutIds(new Set());
    setRestoreResult(null);
    try {
      setCuts(await listCuts(session));
    } catch (e) {
      setCutsError(String(e));
    } finally {
      setLoadingCuts(false);
    }
  }

  async function handleApplyCuts(): Promise<void> {
    if (!session || selectedIds.size === 0) return;
    setApplying(true);
    setApplyResult(null);
    try {
      const result = await applyCuts(session, Array.from(selectedIds), "Corte generado desde módulo de cortes");
      setApplyResult(result);
      setSelectedIds(new Set());
      await loadEligible();
    } catch (e) {
      setEligibleError(String(e));
    } finally {
      setApplying(false);
    }
  }

  async function handleRestoreCuts(): Promise<void> {
    if (!session || selectedCutIds.size === 0) return;
    setRestoring(true);
    setRestoreResult(null);
    try {
      const result = await restoreCuts(session, Array.from(selectedCutIds), "Reactivación desde módulo de cortes");
      setRestoreResult(result);
      setSelectedCutIds(new Set());
      await loadCuts();
    } catch (e) {
      setCutsError(String(e));
    } finally {
      setRestoring(false);
    }
  }

  function toggleId(id: string, set: Set<string>, setter: (s: Set<string>) => void): void {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  }

  function toggleAll(ids: string[], set: Set<string>, setter: (s: Set<string>) => void): void {
    if (ids.every((id) => set.has(id))) setter(new Set());
    else setter(new Set(ids));
  }

  const sectorOptions = useMemo(() => {
    const names = Array.from(new Set(eligible.map((r) => r.sector).filter(Boolean)));
    return names;
  }, [eligible]);

  const monthsOptions = [1, 2, 3, 4, 6];

  const severityColor = (months: number) => {
    if (months >= 6) return "text-red-700 bg-red-100";
    if (months >= 3) return "text-orange-700 bg-orange-100";
    return "text-amber-700 bg-amber-100";
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/administracion")} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100">
            <i className="ri-arrow-left-line text-gray-500" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Cortes de Servicio</h1>
            <p className="text-sm text-gray-500 mt-0.5">Gestión de cortes por morosidad</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        <button
          onClick={() => setTab("elegibles")}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${tab === "elegibles" ? "bg-white text-red-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
        >
          <i className="ri-scissors-line mr-1.5" />Elegibles para corte
          {eligible.length > 0 && <span className="ml-1.5 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">{eligible.length}</span>}
        </button>
        <button
          onClick={() => setTab("cortados")}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${tab === "cortados" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
        >
          <i className="ri-forbid-line mr-1.5" />Servicio cortado
          {cuts.length > 0 && <span className="ml-1.5 text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full">{cuts.length}</span>}
        </button>
      </div>

      {/* ═══ ELEGIBLES ═══ */}
      {tab === "elegibles" && (
        <div className="space-y-4">
          {/* Filtros */}
          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Filtros de búsqueda</div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Meses de mora mínimos</label>
                <select
                  value={monthsFilter}
                  onChange={(e) => setMonthsFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400"
                >
                  {monthsOptions.map((m) => (
                    <option key={m} value={m}>{m} {m === 1 ? "mes" : "meses"}{m === defaultMonths ? " (config)" : ""}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Sector</label>
                <select
                  value={sectorFilter}
                  onChange={(e) => setSectorFilter(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400"
                >
                  <option value="">Todos los sectores</option>
                  {sectors.map((s) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Deuda mínima</label>
                <input
                  type="text"
                  value={amountFilter}
                  onChange={(e) => setAmountFilter(e.target.value)}
                  placeholder="Ej: 50000"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => void loadEligible()}
                disabled={loadingEligible}
                className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-60"
              >
                <i className={loadingEligible ? "ri-loader-4-line animate-spin" : "ri-search-line"} />
                {loadingEligible ? "Buscando..." : "Buscar elegibles"}
              </button>
            </div>
          </div>

          {eligibleError && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{eligibleError}</div>}

          {applyResult && (
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
              <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm">
                <i className="ri-checkbox-circle-line" />
                {applyResult.applied} corte{applyResult.applied !== 1 ? "s" : ""} aplicado{applyResult.applied !== 1 ? "s" : ""} correctamente
              </div>
              {applyResult.errors.length > 0 && (
                <div className="mt-2 text-xs text-red-600">{applyResult.errors.join(", ")}</div>
              )}
            </div>
          )}

          {eligible.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={eligible.length > 0 && eligible.every((r) => selectedIds.has(r.contractId))}
                    onChange={() => toggleAll(eligible.map((r) => r.contractId), selectedIds, setSelectedIds)}
                    className="w-4 h-4 accent-teal-600"
                  />
                  <span className="text-sm font-semibold text-gray-700">
                    {eligible.length} contrato{eligible.length !== 1 ? "s" : ""} elegible{eligible.length !== 1 ? "s" : ""}
                  </span>
                  {selectedIds.size > 0 && (
                    <span className="text-xs text-teal-600 font-medium">{selectedIds.size} seleccionado{selectedIds.size !== 1 ? "s" : ""}</span>
                  )}
                </div>
                {selectedIds.size > 0 && (
                  <button
                    onClick={() => void handleApplyCuts()}
                    disabled={applying}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-60"
                  >
                    <i className={applying ? "ri-loader-4-line animate-spin" : "ri-scissors-line"} />
                    {applying ? "Aplicando..." : `Generar corte (${selectedIds.size})`}
                  </button>
                )}
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-50">
                    <th className="w-10 px-4 py-3"></th>
                    <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Cliente</th>
                    <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Contrato</th>
                    <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Sector</th>
                    <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Mora</th>
                    <th className="text-right text-xs font-semibold text-gray-500 px-4 py-3">Deuda</th>
                    <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {eligible.map((row, i) => (
                    <tr
                      key={row.contractId}
                      onClick={() => toggleId(row.contractId, selectedIds, setSelectedIds)}
                      className={`border-b border-gray-50 cursor-pointer ${selectedIds.has(row.contractId) ? "bg-red-50/60" : "hover:bg-gray-50"} ${i === eligible.length - 1 ? "border-b-0" : ""}`}
                    >
                      <td className="px-4 py-3.5">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(row.contractId)}
                          onChange={() => toggleId(row.contractId, selectedIds, setSelectedIds)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 accent-red-600"
                        />
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="text-sm font-medium text-gray-800">{row.customerName}</div>
                        <div className="text-xs text-gray-400">{row.customerRut}</div>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="text-xs font-mono text-gray-600">{row.contractNumber}</div>
                        <div className="text-xs text-gray-400 truncate max-w-[160px]">{row.address}</div>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600">{row.sector || "-"}</td>
                      <td className="px-4 py-3.5">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${severityColor(row.monthsOverdue)}`}>
                          {row.monthsOverdue} {row.monthsOverdue === 1 ? "mes" : "meses"}
                        </span>
                        <div className="text-xs text-gray-400 mt-0.5">{row.overdueDocuments} doc.</div>
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <div className="text-sm font-bold text-red-600">${row.totalOutstanding.toLocaleString("es-CL")}</div>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          row.customerStatus === "cortado" ? "bg-gray-200 text-gray-600" :
                          row.customerStatus === "moroso" ? "bg-orange-100 text-orange-700" :
                          "bg-emerald-100 text-emerald-700"
                        }`}>
                          {row.customerStatus}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loadingEligible && eligible.length === 0 && !eligibleError && (
            <div className="bg-white rounded-xl border border-gray-100 p-10 text-center text-sm text-gray-400">
              <i className="ri-search-line text-2xl block mb-2" />
              Usa los filtros y haz clic en "Buscar elegibles"
            </div>
          )}
        </div>
      )}

      {/* ═══ CORTADOS ═══ */}
      {tab === "cortados" && (
        <div className="space-y-4">
          {cutsError && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{cutsError}</div>}

          {restoreResult && (
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
              <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm">
                <i className="ri-checkbox-circle-line" />
                {restoreResult.restored} servicio{restoreResult.restored !== 1 ? "s" : ""} reactivado{restoreResult.restored !== 1 ? "s" : ""} correctamente
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={cuts.length > 0 && cuts.every((r) => selectedCutIds.has(r.contractId))}
                  onChange={() => toggleAll(cuts.map((r) => r.contractId), selectedCutIds, setSelectedCutIds)}
                  className="w-4 h-4 accent-teal-600"
                  disabled={loadingCuts || cuts.length === 0}
                />
                <span className="text-sm font-semibold text-gray-700">
                  {loadingCuts ? "Cargando..." : `${cuts.length} contrato${cuts.length !== 1 ? "s" : ""} con servicio cortado`}
                </span>
                {selectedCutIds.size > 0 && (
                  <span className="text-xs text-teal-600 font-medium">{selectedCutIds.size} seleccionado{selectedCutIds.size !== 1 ? "s" : ""}</span>
                )}
              </div>
              {selectedCutIds.size > 0 && (
                <button
                  onClick={() => void handleRestoreCuts()}
                  disabled={restoring}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-60"
                >
                  <i className={restoring ? "ri-loader-4-line animate-spin" : "ri-restart-line"} />
                  {restoring ? "Reactivando..." : `Reactivar (${selectedCutIds.size})`}
                </button>
              )}
            </div>
            {!loadingCuts && cuts.length === 0 ? (
              <div className="text-center py-10 text-sm text-gray-400">
                <i className="ri-check-line text-2xl block mb-2 text-emerald-400" />
                No hay contratos con servicio cortado
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-50">
                    <th className="w-10 px-4 py-3"></th>
                    <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Cliente</th>
                    <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Contrato</th>
                    <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Sector</th>
                    <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Fecha corte</th>
                    <th className="text-right text-xs font-semibold text-gray-500 px-4 py-3">Deuda</th>
                  </tr>
                </thead>
                <tbody>
                  {cuts.map((row, i) => (
                    <tr
                      key={row.contractId}
                      onClick={() => toggleId(row.contractId, selectedCutIds, setSelectedCutIds)}
                      className={`border-b border-gray-50 cursor-pointer ${selectedCutIds.has(row.contractId) ? "bg-teal-50/60" : "hover:bg-gray-50"} ${i === cuts.length - 1 ? "border-b-0" : ""}`}
                    >
                      <td className="px-4 py-3.5">
                        <input
                          type="checkbox"
                          checked={selectedCutIds.has(row.contractId)}
                          onChange={() => toggleId(row.contractId, selectedCutIds, setSelectedCutIds)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 accent-teal-600"
                        />
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="text-sm font-medium text-gray-800">{row.customerName}</div>
                        <div className="text-xs text-gray-400">{row.customerRut}</div>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="text-xs font-mono text-gray-600">{row.contractNumber}</div>
                        <div className="text-xs text-gray-400 truncate max-w-[160px]">{row.address}</div>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600">{row.sector || "-"}</td>
                      <td className="px-4 py-3.5 text-sm text-gray-500">
                        {row.cutDate ? new Date(row.cutDate).toLocaleDateString("es-CL") : "-"}
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <div className="text-sm font-bold text-red-600">${row.totalOutstanding.toLocaleString("es-CL")}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
